import { describeRemote } from './onboarding-diagnosis';
import { useRefreshRemoteHealth, useResubscribe } from './use-onboarding';

import type { ChannelConfigWire, RemoteHealthWire } from './types';
import type { ReactElement } from 'react';

function quando(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${String(min)} min`;
  const h = Math.floor(min / 60);
  return h < 24
    ? `há ${String(h)} h`
    : new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

const QUALIDADE: Record<string, string> = { GREEN: 'alta', YELLOW: 'média', RED: 'baixa' };
const LIMITE: Record<string, string> = {
  TIER_250: '250',
  TIER_1K: '1.000',
  TIER_10K: '10.000',
  TIER_100K: '100.000',
  TIER_UNLIMITED: 'sem limite',
};
const NOME: Record<string, string> = {
  APPROVED: 'aprovado',
  PENDING_REVIEW: 'em análise',
  DECLINED: 'recusado',
  NONE: 'não enviado',
};

function comDestaque(frase: string): ReactElement {
  return (
    <>
      {frase
        .split('**')
        .map((p, i) => (i % 2 === 1 ? <strong key={p + String(i)}>{p}</strong> : p))}
    </>
  );
}

function podeEnviar(v: RemoteHealthWire['canSendMessage']): string {
  if (v === 'AVAILABLE') return 'sim, sem restrição';
  if (v === 'LIMITED') return 'com limites';
  if (v === 'BLOCKED') return 'não — veja acima';
  return '—';
}

/**
 * B1 pendências + B2 «o que a Meta diz» + B3 degradado. A ordem é a tese da aba: primeiro a
 * frase que age (degradado), depois o que falta do tenant (pendências), por fim os fatos.
 */
export function RemoteHealthBlock({
  config,
  remote,
  canManage,
  onReconectar,
}: {
  config: ChannelConfigWire;
  remote: RemoteHealthWire;
  canManage: boolean;
  onReconectar: () => void;
}): ReactElement {
  const refresh = useRefreshRemoteHealth();
  const religar = useResubscribe();
  const { pendencias, degraded } = describeRemote(remote, config.displayName);
  const acaoDegradado = degraded?.action ?? null;

  const fatos: [string, string][] = [
    ['Pode enviar', podeEnviar(remote.canSendMessage)],
    [
      'Conversas por dia',
      remote.messagingLimit === null
        ? '—'
        : `até ${LIMITE[remote.messagingLimit] ?? remote.messagingLimit} iniciadas por você`,
    ],
    [
      'Qualidade',
      remote.qualityRating === null
        ? '—'
        : (QUALIDADE[remote.qualityRating] ?? remote.qualityRating.toLowerCase()),
    ],
    [
      'Nome de exibição',
      `${config.displayName === null ? '' : `«${config.displayName}» · `}${
        remote.nameStatus === null
          ? '—'
          : (NOME[remote.nameStatus] ?? remote.nameStatus.toLowerCase())
      }`,
    ],
    [
      'Empresa',
      remote.businessVerification === 'verified'
        ? 'verificada'
        : remote.businessVerification === null
          ? '—'
          : 'não verificada',
    ],
    [
      'Recebimento',
      remote.subscribed === true
        ? 'ligado · conferido'
        : remote.subscribed === false
          ? 'parou'
          : '—',
    ],
    [
      'Registro do número',
      remote.registered === true
        ? 'registrado'
        : remote.registered === false
          ? 'não registrado'
          : '—',
    ],
  ];

  return (
    <>
      {degraded !== null && (
        <div
          className="mt-3 flex gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-xs leading-relaxed text-red-700"
          role="alert"
        >
          <span className="font-bold">✗</span>
          <div className="min-w-0 flex-1">{comDestaque(degraded.sentence)}</div>
          {canManage && acaoDegradado?.href !== undefined && (
            <a
              className="self-center whitespace-nowrap rounded-md border border-border-strong bg-surface px-2.5 py-1 text-xs text-text-secondary"
              href={acaoDegradado.href}
              target="_blank"
              rel="noreferrer"
            >
              {acaoDegradado.label}
            </a>
          )}
          {canManage && acaoDegradado !== null && acaoDegradado.href === undefined && (
            <button
              type="button"
              className="self-center whitespace-nowrap rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
              disabled={religar.isPending}
              onClick={() => {
                if (acaoDegradado.kind === 'religar') religar.mutate(config.id);
                else onReconectar();
              }}
            >
              {acaoDegradado.label}
            </button>
          )}
        </div>
      )}

      {/* «Já recebe, mas não inicia conversa» só quando é verdade: envio de fato bloqueado e canal
          não degradado — senão contradiz o bloco vermelho acima (achado do revisor, 30/Set). */}
      {pendencias.length > 0 && remote.canSendMessage !== 'AVAILABLE' && degraded === null && (
        <div className="mt-3 flex gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs leading-relaxed text-emerald-700">
          <span className="font-bold">✓</span>
          <div>
            Seu número <strong>já recebe mensagens</strong> e você já pode responder a quem
            escrever. O que ainda não dá: <strong>começar uma conversa</strong> com um cliente, ou
            responder depois de 24 horas. Isso libera com o passo abaixo.
          </div>
        </div>
      )}
      {pendencias.map((p) => (
        <div
          key={p.title}
          className="mt-2 grid gap-3 rounded-md border border-border-default bg-surface px-3.5 py-3 sm:grid-cols-[minmax(0,1fr)_auto]"
        >
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-secondary">{p.who}</div>
            <div className="text-[13px] font-semibold text-text-primary">{p.title}</div>
            <p className="mt-0.5 text-xs leading-relaxed text-text-secondary">{p.why}</p>
          </div>
          <div className="flex min-w-[170px] flex-col gap-1.5">
            {p.action?.href !== undefined && (
              <a
                className="rounded-md bg-accent px-2.5 py-1 text-center text-xs font-medium text-white"
                href={p.action.href}
                target="_blank"
                rel="noreferrer"
              >
                {p.action.label}
              </a>
            )}
            {p.action === null && (
              <span className="rounded-full border border-border-default bg-bg-muted px-2 py-0.5 text-center text-xs text-text-secondary">
                aguardando a Meta
              </span>
            )}
            {canManage && p.verificar && (
              <button
                type="button"
                className="rounded-md border border-border-strong bg-surface px-2.5 py-1 text-xs text-text-secondary disabled:opacity-50"
                disabled={refresh.isPending}
                onClick={() => {
                  refresh.mutate(config.id);
                }}
              >
                Verificar de novo
              </button>
            )}
          </div>
        </div>
      ))}

      {refresh.isError && (
        <p className="mt-2 text-xs text-red-700" role="alert">
          Não deu para consultar a Meta agora: {refresh.error.message}
        </p>
      )}
      {religar.isError && (
        <p className="mt-2 text-xs text-red-700" role="alert">
          Não deu para religar: {religar.error.message}
        </p>
      )}

      <div className="mt-3.5">
        <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-[10px] uppercase tracking-wide text-text-secondary">
            O que a Meta diz sobre este número
          </span>
          <span className="flex items-center gap-2 text-[11px] text-text-secondary">
            consultado {quando(remote.fetchedAt)}
            {canManage && (
              <button
                type="button"
                className="font-medium text-accent disabled:opacity-50"
                disabled={refresh.isPending}
                onClick={() => {
                  refresh.mutate(config.id);
                }}
              >
                Verificar de novo
              </button>
            )}
          </span>
        </div>
        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border-subtle bg-border-subtle sm:grid-cols-4">
          {fatos.map(([k, v]) => (
            <div key={k} className="bg-surface px-3 py-2.5">
              <dt className="text-[10px] uppercase tracking-wide text-text-secondary">{k}</dt>
              <dd className="mt-0.5 text-xs text-text-primary">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </>
  );
}
